/**
 * SC-209: the controls along the bottom of the floating window.
 *
 * Plain `<button>`s, not `GameMenuToolbarIconButton`: that is a MUI `IconButton`, and MUI's tooltips and
 * ripples reach for the opener's `document.body`, which in a picture-in-picture window means they render
 * behind the tab nobody is looking at. The wording and the live/muted/auto-muted reading come from
 * `callControlLabels`, shared with the game-menu bar, so the two toolbars cannot say different things about
 * the same microphone.
 */

import React from 'react'

import {
  micControlLabel,
  micVisualState,
  raiseHandControlLabel,
} from '../../GameMenu/callControlLabels'
import { HandIcon } from '../../ScreenShare/HandRaisedIndicator'
import { menuControlAttrs } from '../../GameMenu/menuAccess'
import { useCameraControlState } from '../../../hooks/useCameraControlState'
import { backgroundProblemTooltip } from '../../../services/backgroundProblemCopy'
import type { PipModel } from './pipModel'
import type { PipAutoOpen } from './pipPreferences'
import {
  AutoOpenGlyph,
  AutoOpenOffGlyph,
  BackToTabGlyph,
  CameraGlyph,
  CameraOffGlyph,
  CloseGlyph,
  MicGlyph,
  MicOffGlyph,
  WarningGlyph,
} from './pipIcons'
import {
  cameraControlWrapStyle,
  cameraWarningBadgeStyle,
  cameraWarningButtonStyle,
  toolbarButtonStyle,
  toolbarStyle,
} from './pipStyles'

export interface PipToolbarProps {
  controls: PipModel['controls']
  /** Raise the tab and close the window — the user wants the full UI back, and one action does both. */
  onBackToTab(): void
  onClose(): void
  autoOpen: PipAutoOpen
  onToggleAutoOpen(): void
}

const BACK_TO_TAB_LABEL = 'Back to tab'
const CLOSE_LABEL = 'Close picture-in-picture'

/**
 * Deliberately not "when I switch tabs": that route exists on Chrome and Edge only (see `usePictureInPicture`),
 * while opening when you start sharing is attempted wherever this toolbar is shown (Document PiP). One toggle
 * governs both, so the wording stays general and stays true wherever it is read.
 */
const AUTO_OPEN_ON_LABEL = 'Opens automatically - click to stop'
const AUTO_OPEN_OFF_LABEL = 'Not opening automatically - click to allow'

export function PipToolbar({
  controls,
  onBackToTab,
  onClose,
  autoOpen,
  onToggleAutoOpen,
}: PipToolbarProps) {
  const micVisual = micVisualState(controls)
  const micLabel = micControlLabel(controls)
  // SC-158: the call bar's camera reading, from the same hook on the opener's window.
  const {
    busy: videoToggleBusy,
    blocked: cameraBlocked,
    backgroundProblem,
    waiting: cameraWaiting,
    disabled: cameraDisabled,
    label: cameraLabel,
  } = useCameraControlState(controls.videoEnabled)
  const raiseHand = controls.raiseHand

  return (
    <div style={toolbarStyle} data-testid="pip-toolbar">
      <button
        type="button"
        onClick={controls.toggleAudio}
        title={micLabel}
        aria-label={micLabel}
        aria-pressed={controls.audioEnabled}
        data-mic-state={micVisual}
        {...menuControlAttrs('call.mic')}
        style={toolbarButtonStyle(
          micVisual === 'live' ? 'live' : micVisual === 'auto-muted' ? 'warning' : 'off'
        )}
      >
        {controls.audioEnabled ? <MicGlyph /> : <MicOffGlyph />}
      </button>

      <span style={cameraControlWrapStyle}>
        <button
          type="button"
          onClick={controls.toggleVideo}
          disabled={cameraDisabled}
          title={cameraLabel}
          aria-label={cameraLabel}
          aria-pressed={controls.videoEnabled}
          {...menuControlAttrs('call.camera')}
          data-camera-state={cameraWaiting ? 'waiting' : controls.videoEnabled ? 'on' : 'off'}
          data-camera-busy={videoToggleBusy ? 'true' : 'false'}
          data-camera-blocked={cameraBlocked ? 'true' : 'false'}
          style={toolbarButtonStyle(
            cameraWaiting ? 'warning' : controls.videoEnabled ? 'live' : 'off',
            { disabled: cameraDisabled }
          )}
        >
          {controls.videoEnabled ? <CameraGlyph /> : <CameraOffGlyph />}
        </button>
        {/* The call bar's warning opens a MUI menu of fixes, which would open behind the tab here. So the
            reason is the tooltip, and a click goes back to the tab, where that menu is. */}
        {backgroundProblem && (
          <button
            type="button"
            onClick={onBackToTab}
            title={backgroundProblemTooltip(backgroundProblem)}
            aria-label={backgroundProblemTooltip(backgroundProblem)}
            data-testid="pip-camera-background-warning"
            {...menuControlAttrs('call.background-warning')}
            data-cause={backgroundProblem.cause}
            style={cameraWarningButtonStyle}
          >
            <span style={cameraWarningBadgeStyle}>
              <WarningGlyph size={12} />
            </span>
          </button>
        )}
      </span>

      {/* SC-415: absent whenever the call bar's is, so the window never offers a raise the server
          refuses. `HandIcon` is a bare react-icons svg, with no styles to route into this document. */}
      {raiseHand && (
        <button
          type="button"
          onClick={() => raiseHand.toggle()}
          title={raiseHandControlLabel(raiseHand)}
          aria-label={raiseHandControlLabel(raiseHand)}
          aria-pressed={raiseHand.raised}
          data-hand-state={raiseHand.raised ? 'raised' : 'down'}
          data-testid="pip-raise-hand"
          {...menuControlAttrs('call.raise-hand')}
          style={toolbarButtonStyle(raiseHand.raised ? 'warning' : 'neutral')}
        >
          <HandIcon size={14} />
        </button>
      )}

      {/* The window that opens itself is the right place to offer to stop: there is no in-room settings
          surface for per-person preferences, and this is the only screen the behaviour is about. */}
      <button
        type="button"
        onClick={onToggleAutoOpen}
        title={autoOpen === 'on' ? AUTO_OPEN_ON_LABEL : AUTO_OPEN_OFF_LABEL}
        aria-label={autoOpen === 'on' ? AUTO_OPEN_ON_LABEL : AUTO_OPEN_OFF_LABEL}
        aria-pressed={autoOpen === 'on'}
        data-auto-open={autoOpen}
        data-testid="pip-auto-open"
        {...menuControlAttrs('pip.auto-open')}
        style={toolbarButtonStyle(autoOpen === 'on' ? 'live' : 'neutral')}
      >
        {autoOpen === 'on' ? <AutoOpenGlyph /> : <AutoOpenOffGlyph />}
      </button>

      <button
        type="button"
        onClick={onBackToTab}
        title={BACK_TO_TAB_LABEL}
        aria-label={BACK_TO_TAB_LABEL}
        {...menuControlAttrs('pip.back-to-tab')}
        style={toolbarButtonStyle('neutral')}
      >
        <BackToTabGlyph />
      </button>

      <button
        type="button"
        onClick={onClose}
        title={CLOSE_LABEL}
        aria-label={CLOSE_LABEL}
        {...menuControlAttrs('pip.close')}
        style={toolbarButtonStyle('neutral')}
      >
        <CloseGlyph />
      </button>
    </div>
  )
}

export default PipToolbar
